import React, { useState, useEffect, useRef } from 'react';
import {
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  XMarkIcon,
  UserGroupIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import useFormFields from '../hooks/useFormFields';
import { validatePhoneNumber, handlePhoneInputChange } from '../utils/phoneValidation';
import api, { authApi } from '../config/api';
import { jwtDecode } from 'jwt-decode';
const LeadForm = ({ lead, onSubmit, onCancel }) => {
  const { fields, loading } = useFormFields();
  const [leadSources, setLeadSources] = useState([]);
  const [loadingSources, setLoadingSources] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    source: 'website',
    budget: '',
    notes: '',
    status: 'new',
    priority: 'medium',
    value: 0,
    assignedTo: [], // Array of employee IDs
    customFields: {}
  });
  const [phoneError, setPhoneError] = useState('');
  const [customFieldPhoneErrors, setCustomFieldPhoneErrors] = useState({});
  const employeeDropdownRef = useRef(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);

  // Close employee dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (employeeDropdownRef.current && !employeeDropdownRef.current.contains(event.target)) {
        setShowEmployeeDropdown(false);
      }
      if (openDropdownId && !event.target.closest('.custom-dropdown-container')) {
        setOpenDropdownId(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDropdownId]);

  // Built-in fields that should be stored as direct properties
  const builtInFields = ['name', 'email', 'phone', 'source', 'budget', 'notes', 'status', 'priority', 'value'];

  // Load lead sources
  useEffect(() => {
    loadLeadSources();
    loadEmployees();
  }, []);

  const loadLeadSources = async () => {
    try {
      setLoadingSources(true);
      const response = await api.get('/lead-sources');
      if (response.data.success) {
        setLeadSources(response.data.sources || []);
      }
    } catch (error) {
      console.error('Error loading lead sources:', error);
      // Initialize default sources if error
      try {
        await api.post('/lead-sources/initialize');
        const retryResponse = await api.get('/lead-sources');
        if (retryResponse.data.success) {
          setLeadSources(retryResponse.data.sources || []);
        }
      } catch (initError) {
        console.error('Error initializing sources:', initError);
      }
    } finally {
      setLoadingSources(false);
    }
  };


  // Load employees for assignment
  const loadEmployees = async () => {
    try {
      setLoadingEmployees(true);
      
      // Get token and decode it to find companyId
      const token = localStorage.getItem('authToken');
      let companyId = localStorage.getItem('selectedCompanyId') || localStorage.getItem('companyId');
      
      if (token) {
        try {
          const decoded = jwtDecode(token.replace(/['"]/g, '').trim());
          const decodedCompanyId = decoded.companyId || decoded.company?._id || decoded.company || decoded.id;
          if (decodedCompanyId) {
            companyId = typeof decodedCompanyId === 'object' ? (decodedCompanyId._id || decodedCompanyId.id) : decodedCompanyId;
          }
        } catch (err) {
          console.error('Failed to decode token in LeadForm loadEmployees:', err);
        }
      }

      console.log('👥 LeadForm: Loading employees for company:', companyId);

      let response;
      if (companyId) {
        try {
          response = await authApi.get(`/companies/${companyId}/employees`);
        } catch (err) {
          console.warn('Failed to fetch by company endpoint, falling back to /employees:', err);
          try {
            response = await authApi.get('/employees');
          } catch (e) {
            response = await api.get('/employees');
          }
        }
      } else {
        try {
          response = await authApi.get('/employees');
        } catch (err) {
          response = await api.get('/employees');
        }
      }
      
      if (response && response.data) {
        let employeeList = [];
        if (Array.isArray(response.data)) {
          employeeList = response.data;
        } else if (response.data.success && response.data.data) {
          employeeList = response.data.data;
        } else if (response.data.employees && Array.isArray(response.data.employees)) {
          employeeList = response.data.employees;
        }

        // Apply client-side filter by companyId as a fallback guarantee
        if (companyId && Array.isArray(employeeList)) {
          employeeList = employeeList.filter(emp => {
            const empCompanyId = emp.companyId || emp.company;
            const cleanEmpCompanyId = typeof empCompanyId === 'object' ? (empCompanyId?._id || empCompanyId?.id) : empCompanyId;
            return String(cleanEmpCompanyId) === String(companyId);
          });
        }

        setEmployees(employeeList || []);
        console.log(`✅ LeadForm: Loaded ${employeeList?.length || 0} employees`);
      }
    } catch (error) {
      console.error('Error loading employees:', error);
    } finally {
      setLoadingEmployees(false);
    }
  };

  // Toggle employee selection
  const toggleEmployeeSelection = (employeeId) => {
    setFormData(prev => {
      const currentAssigned = prev.assignedTo || [];
      const isSelected = currentAssigned.includes(employeeId);

      if (isSelected) {
        return {
          ...prev,
          assignedTo: currentAssigned.filter(id => id !== employeeId)
        };
      } else {
        return {
          ...prev,
          assignedTo: [...currentAssigned, employeeId]
        };
      }
    });
  };

  // Remove employee from selection
  const removeEmployee = (employeeId) => {
    setFormData(prev => ({
      ...prev,
      assignedTo: (prev.assignedTo || []).filter(id => id !== employeeId)
    }));
  };

  // Get selected employees details
  const getSelectedEmployees = () => {
    return employees.filter(emp => (formData.assignedTo || []).includes(emp._id));
  };

  // Initialize form data with built-in fields and dynamic fields
  useEffect(() => {
    const initialData = {
      name: '',
      email: '',
      phone: '',
      title: '',
      source: 'website',
      budget: '',
      notes: '',
      status: 'new',
      priority: 'medium',
      value: 0,
      assignedTo: [],
      customFields: {}
    };

    // Initialize custom fields from dynamic form fields
    fields.forEach(field => {
      if (!builtInFields.includes(field.name)) {
        initialData.customFields[field.name] = '';
      }
    });

    if (lead) {
      // Populate with existing lead data
      builtInFields.forEach(key => {
        if (lead[key] !== undefined) {
          initialData[key] = lead[key];
        }
      });

      // Handle assignedTo - convert to array if needed
      if (lead.assignedTo) {
        if (Array.isArray(lead.assignedTo)) {
          initialData.assignedTo = lead.assignedTo.map(emp =>
            typeof emp === 'object' ? emp._id : emp
          );
        } else {
          initialData.assignedTo = [typeof lead.assignedTo === 'object' ? lead.assignedTo._id : lead.assignedTo];
        }
      }

      // Populate custom fields
      if (lead.customFields) {
        Object.keys(lead.customFields).forEach(key => {
          initialData.customFields[key] = lead.customFields[key];
        });
      }

      // Also check for custom fields stored directly on lead (for backward compatibility)
      fields.forEach(field => {
        if (!builtInFields.includes(field.name) && lead[field.name] !== undefined) {
          initialData.customFields[field.name] = lead[field.name];
        }
      });
    }

    setFormData(initialData);
  }, [lead, fields]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (builtInFields.includes(name)) {
      // Handle built-in fields
      if (name === 'phone') {
        // Validate phone number
        const validation = validatePhoneNumber(value);
        if (value && !validation.isValid) {
          setPhoneError(validation.message);
        } else {
          setPhoneError('');
        }
        setFormData({
          ...formData,
          [name]: validation.formattedNumber || value
        });
      } else {
        setFormData({
          ...formData,
          [name]: value
        });
      }
    } else {
      // Handle custom fields - check if it's a phone field
      const field = fields.find(f => f.name === name);
      if (field && field.type === 'phone') {
        const validation = validatePhoneNumber(value);
        if (value && !validation.isValid) {
          setCustomFieldPhoneErrors({
            ...customFieldPhoneErrors,
            [name]: validation.message
          });
        } else {
          setCustomFieldPhoneErrors({
            ...customFieldPhoneErrors,
            [name]: ''
          });
        }
        setFormData({
          ...formData,
          customFields: {
            ...formData.customFields,
            [name]: validation.formattedNumber || value
          }
        });
      } else {
        setFormData({
          ...formData,
          customFields: {
            ...formData.customFields,
            [name]: value
          }
        });
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate that at least email or phone is provided
    if (!formData.email && !formData.phone) {
      alert('Please provide either email or phone number');
      return;
    }

    // Validate phone numbers before submission
    if (formData.phone) {
      const phoneValidation = validatePhoneNumber(formData.phone);
      if (!phoneValidation.isValid) {
        setPhoneError(phoneValidation.message);
        return;
      }
    }

    // Validate custom phone fields
    let hasPhoneErrors = false;
    const newPhoneErrors = {};
    fields.forEach(field => {
      if (field.type === 'phone' && formData.customFields[field.name]) {
        const validation = validatePhoneNumber(formData.customFields[field.name]);
        if (!validation.isValid) {
          newPhoneErrors[field.name] = validation.message;
          hasPhoneErrors = true;
        }
      }
    });

    if (hasPhoneErrors) {
      setCustomFieldPhoneErrors(newPhoneErrors);
      return;
    }

    // Prepare the final lead data
    const leadData = {
      ...formData,
      // Ensure customFields is properly structured
      customFields: formData.customFields || {},
      // Include assigned employees
      assignedTo: formData.assignedTo || []
    };

    // Debug log to check custom fields data
    console.log('🔍 Submitting lead with custom fields:', leadData.customFields);
    console.log('🔍 All form fields:', fields.map(f => f.name));
    console.log('🔍 Full lead data:', leadData);

    // Add assigned employees details for quick access
    if (formData.assignedTo && formData.assignedTo.length > 0) {
      leadData.assignedEmployees = formData.assignedTo.map(empId => {
        const emp = employees.find(e => e._id === empId || e._id?.toString() === empId?.toString());
        console.log('🔍 Finding employee for ID:', empId, 'Found:', emp?.teamMemberName || emp?.name);
        return emp ? {
          employeeId: emp._id,
          name: emp.teamMemberName || emp.name || 'Unknown',
          email: emp.teamMemberEmail || emp.email || '',
          phone: emp.emergencyMobileNumber || emp.phone || emp.teamMemberPhone || ''
        } : null;
      }).filter(Boolean);

      console.log('📋 Prepared assignedEmployees:', leadData.assignedEmployees);
    }

    // Handle select field labels for custom fields
    fields.forEach(field => {
      if (field.type === 'select' && field.options && formData.customFields[field.name]) {
        const selectedOption = field.options.find(option => option.value === formData.customFields[field.name]);
        if (selectedOption) {
          leadData.customFields[field.name] = selectedOption.label;
        }
      }
    });

    onSubmit(leadData);
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 w-full bg-white">
      {/* Header - Mobile */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-3 shrink-0 sm:hidden">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center shrink-0">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={lead ? "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" : "M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"}></path>
              </svg>
            </div>
            <div>
              <h2 className="text-base font-bold text-white mb-0.5 leading-tight">
                {lead ? 'Edit Lead' : 'Add New Lead'}
              </h2>
              <p className="text-indigo-100 text-[10px] leading-tight">
                {lead ? 'Update lead details' : 'Create new lead & assign'}
              </p>
            </div>
          </div>

          <button
            onClick={onCancel}
            className="w-7 h-7 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-colors shrink-0"
          >
            <XMarkIcon className="w-3.5 h-3.5 text-white" />
          </button>
        </div>
      </div>

      {/* Header - Desktop */}
      <div className="hidden sm:flex justify-between items-center px-6 py-4 border-b border-gray-100 shrink-0">
        <h3 className="text-xl font-semibold text-gray-900">
          {lead ? 'Edit Lead' : 'Add New Lead'}
        </h3>
        <button
          onClick={onCancel}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>

      {/* Body */}
      <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 bg-slate-50/50">
        <div className="flex-1 overflow-y-auto px-4 py-4 md:px-6 md:py-5 space-y-4 pb-32 sm:pb-8">
          <div className="grid grid-cols-1 gap-3 md:gap-4 sm:grid-cols-2">

            {/* Full Name - Always First */}
            <div>
              <label htmlFor="name" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">
                Full Name *
              </label>
              <input
                type="text"
                name="name"
                id="name"
                className="w-full px-3 py-2 text-sm sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white hover:border-gray-400 transition-colors"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>

            {/* Dynamic Custom Fields - Right After Full Name */}
            {fields.map((field) => (
              <div key={field._id} className={field.type === 'textarea' ? 'sm:col-span-2' : ''}>
                <label htmlFor={field.name} className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">
                  {field.label} {field.required && '*'}
                </label>

                {field.type === 'text' && (
                  <input
                    type="text"
                    name={field.name}
                    id={field.name}
                    className="w-full px-3 py-2 text-sm sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white hover:border-gray-400 transition-colors"
                    value={formData.customFields[field.name] || ''}
                    onChange={handleChange}
                    placeholder={field.placeholder}
                    required={field.required}
                  />
                )}

                {field.type === 'email' && (
                  <input
                    type="email"
                    name={field.name}
                    id={field.name}
                    className="w-full px-3 py-2 text-sm sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white hover:border-gray-400 transition-colors"
                    value={formData.customFields[field.name] || ''}
                    onChange={handleChange}
                    placeholder={field.placeholder}
                    required={field.required}
                  />
                )}

                {field.type === 'phone' && (
                  <div>
                    <input
                      type="tel"
                      name={field.name}
                      id={field.name}
                      className={`w-full px-3 py-2 text-sm sm:text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white hover:border-gray-400 transition-colors ${customFieldPhoneErrors[field.name] ? 'border-red-500' : 'border-gray-300'
                        }`}
                      value={formData.customFields[field.name] || ''}
                      onChange={handleChange}
                      placeholder={field.placeholder || "Enter 10-digit mobile number"}
                      maxLength="10"
                      required={field.required}
                    />
                    {customFieldPhoneErrors[field.name] && (
                      <p className="mt-1 text-xs text-red-600">{customFieldPhoneErrors[field.name]}</p>
                    )}
                  </div>
                )}

                {field.type === 'number' && (
                  <input
                    type="number"
                    name={field.name}
                    id={field.name}
                    className="w-full px-3 py-2 text-sm sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white hover:border-gray-400 transition-colors"
                    value={formData.customFields[field.name] || ''}
                    onChange={handleChange}
                    placeholder={field.placeholder}
                    required={field.required}
                  />
                )}

                {field.type === 'textarea' && (
                  <textarea
                    name={field.name}
                    id={field.name}
                    rows={3}
                    className="w-full px-3 py-2 text-sm sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white hover:border-gray-400 transition-colors"
                    value={formData.customFields[field.name] || ''}
                    onChange={handleChange}
                    placeholder={field.placeholder}
                    required={field.required}
                  />
                )}

                {field.type === 'select' && (
                  <>
                    <div className="hidden sm:block">
                      <select
                        name={field.name}
                        id={field.name}
                        className="w-full px-3 py-2 text-sm sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white hover:border-gray-400 transition-colors"
                        value={formData.customFields[field.name] || ''}
                        onChange={handleChange}
                        required={field.required}
                      >
                        <option value="">{field.placeholder || `Select ${field.label}...`}</option>
                        {field.options?.map((option, index) => (
                          <option key={index} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="sm:hidden relative custom-dropdown-container">
                      <div
                        className="w-full px-3 py-2.5 text-sm sm:text-sm border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 bg-white cursor-pointer flex justify-between items-center min-h-[42px] hover:border-gray-400 transition-colors"
                        onClick={() => setOpenDropdownId(openDropdownId === field.name ? null : field.name)}
                      >
                        <span className={`text-sm ${formData.customFields[field.name] ? 'text-gray-900' : 'text-gray-500'}`}>
                          {formData.customFields[field.name] 
                            ? (field.options?.find(o => o.value === formData.customFields[field.name])?.label || formData.customFields[field.name]) 
                            : (field.placeholder || `Select ${field.label}...`)}
                        </span>
                        <div className="text-gray-500 pointer-events-none shrink-0 ml-2">
                          <svg className={`w-4 h-4 transition-transform duration-200 ${openDropdownId === field.name ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                          </svg>
                        </div>
                      </div>

                      {openDropdownId === field.name && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl max-h-48 overflow-y-auto divide-y divide-gray-50">
                          <div
                            className="px-4 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors text-gray-500 italic text-sm"
                            onClick={() => {
                              handleChange({ target: { name: field.name, value: '' } });
                              setOpenDropdownId(null);
                            }}
                          >
                            Clear Selection
                          </div>
                          {field.options?.map((option, index) => (
                            <div
                              key={index}
                              className={`px-4 py-2.5 cursor-pointer transition-colors text-sm ${formData.customFields[field.name] === option.value ? 'bg-blue-50 text-blue-700 font-semibold' : 'hover:bg-gray-50 text-gray-700'}`}
                              onClick={() => {
                                handleChange({ target: { name: field.name, value: option.value } });
                                setOpenDropdownId(null);
                              }}
                            >
                              {option.label}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}

                {field.type === 'date' && (
                  <input
                    type="date"
                    name={field.name}
                    id={field.name}
                    className="w-full px-3 py-2 text-sm sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white hover:border-gray-400 transition-colors"
                    value={formData.customFields[field.name] || ''}
                    onChange={handleChange}
                    required={field.required}
                  />
                )}
              </div>
            ))}

            {/* Other Built-in Fields */}
            <div>
              <label htmlFor="email" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">
                Email Address
              </label>
              <input
                type="email"
                name="email"
                id="email"
                className="w-full px-3 py-2 text-sm sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white hover:border-gray-400 transition-colors"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter email address"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                id="phone"
                className={`w-full px-3 py-2 text-sm sm:text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white hover:border-gray-400 transition-colors ${phoneError ? 'border-red-500' : 'border-gray-300'
                  }`}
                value={formData.phone}
                onChange={handleChange}
                placeholder="Enter 10-digit mobile number"
                maxLength="10"
              />
              {phoneError && (
                <p className="mt-1 text-xs text-red-600">{phoneError}</p>
              )}
            </div>

            <div>
              <label htmlFor="source" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">
                Lead Source
              </label>
              <div className="hidden sm:block">
                <select
                  id="source"
                  name="source"
                  className="w-full px-3 py-2 text-sm sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white hover:border-gray-400 transition-colors"
                  value={formData.source}
                  onChange={handleChange}
                  disabled={loadingSources}
                >
                  {loadingSources ? (
                    <option>Loading sources...</option>
                  ) : leadSources.length > 0 ? (
                    leadSources.map((source) => (
                      <option key={source._id} value={source.name}>
                        {source.icon} {source.label}
                      </option>
                    ))
                  ) : (
                    <>
                      <option value="website">🌐 Website</option>
                      <option value="referral">👥 Referral</option>
                      <option value="social_media">📱 Social Media</option>
                      <option value="email_campaign">📧 Email Campaign</option>
                      <option value="phone_call">📞 Phone Call</option>
                      <option value="other">📍 Other</option>
                    </>
                  )}
                </select>
              </div>

              <div className="sm:hidden relative custom-dropdown-container">
                <div
                  className="w-full px-3 py-2.5 text-sm sm:text-sm border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 bg-white cursor-pointer flex justify-between items-center min-h-[42px] hover:border-gray-400 transition-colors"
                  onClick={() => !loadingSources && setOpenDropdownId(openDropdownId === 'source' ? null : 'source')}
                >
                  <span className={`text-sm ${formData.source ? 'text-gray-900' : 'text-gray-500'}`}>
                    {loadingSources ? 'Loading sources...' : (
                      leadSources.length > 0 
                        ? (leadSources.find(s => s.name === formData.source) ? `${leadSources.find(s => s.name === formData.source).icon} ${leadSources.find(s => s.name === formData.source).label}` : formData.source)
                        : (
                          formData.source === 'website' ? '🌐 Website' :
                          formData.source === 'referral' ? '👥 Referral' :
                          formData.source === 'social_media' ? '📱 Social Media' :
                          formData.source === 'email_campaign' ? '📧 Email Campaign' :
                          formData.source === 'phone_call' ? '📞 Phone Call' :
                          formData.source === 'other' ? '📍 Other' : formData.source
                        )
                    )}
                  </span>
                  <div className="text-gray-500 pointer-events-none shrink-0 ml-2">
                    <svg className={`w-4 h-4 transition-transform duration-200 ${openDropdownId === 'source' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </div>
                </div>

                {openDropdownId === 'source' && !loadingSources && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl max-h-48 overflow-y-auto divide-y divide-gray-50">
                    {leadSources.length > 0 ? (
                      leadSources.map((source) => (
                        <div
                          key={source._id}
                          className={`px-4 py-2.5 cursor-pointer transition-colors text-sm ${formData.source === source.name ? 'bg-blue-50 text-blue-700 font-semibold' : 'hover:bg-gray-50 text-gray-700'}`}
                          onClick={() => {
                            handleChange({ target: { name: 'source', value: source.name } });
                            setOpenDropdownId(null);
                          }}
                        >
                          {source.icon} {source.label}
                        </div>
                      ))
                    ) : (
                      [
                        { value: 'website', label: '🌐 Website' },
                        { value: 'referral', label: '👥 Referral' },
                        { value: 'social_media', label: '📱 Social Media' },
                        { value: 'email_campaign', label: '📧 Email Campaign' },
                        { value: 'phone_call', label: '📞 Phone Call' },
                        { value: 'other', label: '📍 Other' }
                      ].map((source) => (
                        <div
                          key={source.value}
                          className={`px-4 py-2.5 cursor-pointer transition-colors text-sm ${formData.source === source.value ? 'bg-blue-50 text-blue-700 font-semibold' : 'hover:bg-gray-50 text-gray-700'}`}
                          onClick={() => {
                            handleChange({ target: { name: 'source', value: source.value } });
                            setOpenDropdownId(null);
                          }}
                        >
                          {source.label}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="sm:col-span-2">
              <label htmlFor="budget" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">
                Budget Range
              </label>
              <input
                type="text"
                name="budget"
                id="budget"
                className="w-full px-3 py-2 text-sm sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white hover:border-gray-400 transition-colors"
                value={formData.budget}
                onChange={handleChange}
                placeholder="e.g., ₹1,00,000 - ₹2,50,000 or ₹75,000"
              />
            </div>

            {/* Employee Assignment - Multi-select */}
            <div className="sm:col-span-2">
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">
                <div className="flex items-center gap-2">
                  <UserGroupIcon className="h-4 w-4 text-gray-500" />
                  Assign to Employees
                </div>
              </label>

              {/* Selected Employees Tags */}
              {getSelectedEmployees().length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {getSelectedEmployees().map(emp => (
                    <span
                      key={emp._id}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 border border-blue-100 text-blue-800 rounded-full text-xs font-medium"
                    >
                      <span className="w-5 h-5 bg-blue-600 text-white rounded-full flex items-center justify-center text-[10px] font-bold">
                        {(emp.teamMemberName || emp.name || 'E').charAt(0).toUpperCase()}
                      </span>
                      {emp.teamMemberName || emp.name}
                      <button
                        type="button"
                        onClick={() => removeEmployee(emp._id)}
                        className="ml-0.5 text-blue-500 hover:text-blue-700 transition-colors"
                      >
                        <XMarkIcon className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Employee Dropdown */}
              <div className="relative" ref={employeeDropdownRef}>
                <button
                  type="button"
                  onClick={() => setShowEmployeeDropdown(!showEmployeeDropdown)}
                  className="w-full px-3 py-2.5 text-sm sm:text-sm border border-gray-300 rounded-lg text-left bg-white hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent flex items-center justify-between transition-colors"
                >
                  <span className="text-gray-500">
                    {loadingEmployees
                      ? 'Loading employees...'
                      : formData.assignedTo?.length > 0
                        ? `${formData.assignedTo.length} employee(s) selected`
                        : 'Select employees to assign...'}
                  </span>
                  <svg className={`h-5 w-5 text-gray-400 transition-transform ${showEmployeeDropdown ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {showEmployeeDropdown && !loadingEmployees && (
                  <div className="absolute z-55 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl max-h-48 md:max-h-60 overflow-y-auto divide-y divide-gray-50 animate-fadeIn">
                    {employees.length === 0 ? (
                      <div className="px-4 py-3 text-gray-500 text-sm">
                        No employees found
                      </div>
                    ) : (
                      employees.map(emp => {
                        const isSelected = (formData.assignedTo || []).includes(emp._id);
                        return (
                          <div
                            key={emp._id}
                            onClick={() => toggleEmployeeSelection(emp._id)}
                            className={`px-4 py-3 cursor-pointer flex items-center justify-between hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50/50' : ''
                              }`}
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 ${isSelected ? 'bg-blue-600' : 'bg-gray-400'
                                }`}>
                                {(emp.teamMemberName || emp.name || 'E').charAt(0).toUpperCase()}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="font-semibold text-gray-900 text-sm leading-tight truncate">
                                  {emp.teamMemberName || emp.name}
                                </div>
                                <div className="text-xs text-gray-500 leading-tight mt-0.5 truncate">
                                  {emp.teamMemberEmail || emp.email} • {emp.role || 'Employee'}
                                </div>
                              </div>
                            </div>
                            {isSelected && (
                              <CheckIcon className="h-4 w-4 text-blue-600 flex-shrink-0 ml-2" />
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
              <p className="mt-1.5 text-xs text-gray-500">
                Select one or more employees. Lead will appear on their dashboard.
              </p>
            </div>

            <div className="sm:col-span-2">
              <label htmlFor="notes" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                name="notes"
                id="notes"
                rows={3}
                className="w-full px-3 py-2 text-sm sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white hover:border-gray-400 transition-colors"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Add any additional notes or comments..."
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-2 md:space-x-3 px-4 py-3 md:px-6 md:py-4 border-t border-gray-100 bg-gray-50 shrink-0 rounded-b-none sm:rounded-b-xl">
          <button
            type="button"
            onClick={onCancel}
            className="px-5 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors font-medium shadow-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-5 py-2 text-sm bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all font-medium shadow-md hover:shadow-lg"
          >
            {lead ? 'Update Lead' : 'Add Lead'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default LeadForm;
